import React, { useState } from 'react';
import axios from 'axios';
import { useMercenary } from '../../contexts/MercenaryContext';
import { useSocket } from '../../contexts/SocketContext';
import { evaluateSafeFormula } from '../../utils/safeFormulaEvaluator';
import HoldButton from '../Shared/HoldButton';
import styles from './BuildingDetailModal.module.css';

interface Building {
  name: string;
  level: number;
  description: string;
  benefit: string;
}

interface BuildingDetailModalProps {
  building: Building;
  guildLevel: number;
  guildId: number;
  userRole: string;
  onClose: () => void;
  onUpdate: () => void;
}

const BuildingDetailModal: React.FC<BuildingDetailModalProps> = ({
  building,
  guildLevel,
  guildId,
  userRole,
  onClose,
  onUpdate
}) => {
  const { mercenary } = useMercenary();
  const { socket } = useSocket();
  const [contributing, setContributing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [visitingLibrary, setVisitingLibrary] = useState(false);
  const [currentMercenary, setCurrentMercenary] = useState<any>(null);
  
  // Contribution state
  const [silverAmount, setSilverAmount] = useState<string>('');
  const [roundsAmount, setRoundsAmount] = useState<string>('');
  const [ironAmount, setIronAmount] = useState<string>('');
  const [firwoodAmount, setFirwoodAmount] = useState<string>('');

  // Building costs from database
  const [buildingCosts, setBuildingCosts] = useState<any>(null);
  // Already-contributed progress toward current target level
  const [progress, setProgress] = useState<{ silver: number; rounds: number; materials: Record<string, number> }>({ silver: 0, rounds: 0, materials: {} });
  const [progressLoading, setProgressLoading] = useState(true);

  // Auto-clear success message after 3 seconds
  React.useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(t);
  }, [success]);

  // Refresh mercenary data when modal opens to get latest guildId and Inventory
  const refreshMercenary = React.useCallback(async () => {
    if (!mercenary?.id) return;
    try {
      const response = await axios.get(`/api/mercenary/set-active/${mercenary.id}`, { withCredentials: true });
      setCurrentMercenary(response.data);
    } catch (err) {
      console.error('Failed to refresh mercenary data:', err);
    }
  }, [mercenary?.id]); // only depend on the ID, not the full object

  React.useEffect(() => {
    refreshMercenary();
  }, [refreshMercenary]);

  // Listen to socket updates so "Available Resources" refreshes live
  React.useEffect(() => {
    if (!socket || !mercenary?.id) return;
    const handleStatsUpdate = (data: { mercenaryId: number; updatedStats: any }) => {
      if (data.mercenaryId === mercenary.id) {
        setCurrentMercenary((prev: any) => prev ? { ...prev, ...data.updatedStats } : prev);
      }
    };
    socket.on('update_mercenary_stats', handleStatsUpdate);
    return () => { socket.off('update_mercenary_stats', handleStatsUpdate); };
  }, [socket, mercenary?.id]);

  // Use currentMercenary (refreshed) or fallback to mercenary from context
  const activeMercenary = currentMercenary || mercenary;

  // Fetch building costs from database
  React.useEffect(() => {
    const fetchCosts = async () => {
      try {
        const response = await axios.get('/api/guild/buildings/config', { withCredentials: true });
        setBuildingCosts(response.data);
      } catch (err) {
        console.error('Failed to fetch building costs', err);
      }
    };
    fetchCosts();
  }, []);

  // Fetch existing contribution progress
  const fetchProgress = React.useCallback(async () => {
    const buildingTypeMap: Record<string, string> = {
      'Training Ground': 'trainingGround',
      'Infirmary': 'infirmary',
      'Library': 'library',
      'Warehouse': 'warehouse',
      'Sleeping Hall': 'sleepingHall'
    };
    const bType = buildingTypeMap[building.name];
    if (!bType) return;
    try {
      setProgressLoading(true);
      const resp = await axios.get(`/api/guild/buildings/progress?buildingType=${bType}&currentLevel=${building.level}`, { withCredentials: true });
      setProgress(resp.data);
    } catch (err) {
      console.error('Failed to fetch building progress', err);
    } finally {
      setProgressLoading(false);
    }
  }, [building.name, building.level]);

  React.useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  const canContribute = building.level < guildLevel && building.level < 10;

  // Get total costs for the next level from database config (null while loading)
  const getCostPerLevel = (): { silver: number; rounds: number; iron: number; firwood: number } | null => {
    if (!buildingCosts) return null;

    const buildingTypeMap: Record<string, string> = {
      'Training Ground': 'trainingGround',
      'Infirmary': 'infirmary',
      'Library': 'library',
      'Warehouse': 'warehouse',
      'Sleeping Hall': 'sleepingHall'
    };

    const buildingType = buildingTypeMap[building.name];
    const configs = buildingCosts[buildingType];
    if (!configs) return null;

    const targetLevel = building.level + 1;
    const levelConfig = configs.find((c: any) => c.level === targetLevel);
    if (!levelConfig) return null;

    return {
      silver: levelConfig.silverCost,
      rounds: levelConfig.roundsCost,
      iron: levelConfig.materialCosts.Iron || 0,
      firwood: levelConfig.materialCosts.Firwood || 0
    };
  };

  const costPerLevel = getCostPerLevel();

  // Remaining needed = total required minus already contributed
  const remaining = costPerLevel ? {
    silver: Math.max(0, costPerLevel.silver - progress.silver),
    rounds: Math.max(0, costPerLevel.rounds - progress.rounds),
    iron: Math.max(0, costPerLevel.iron - (progress.materials.Iron || 0)),
    firwood: Math.max(0, costPerLevel.firwood - (progress.materials.Firwood || 0))
  } : null;

  const handleContribute = async () => {
    if (!activeMercenary) {
      setError('No active mercenary found');
      return;
    }

    if (!activeMercenary.guildId) {
      setError('Your active mercenary is not in a guild');
      return;
    }

    const silver = Number(silverAmount) || 0;
    const rounds = Number(roundsAmount) || 0;
    const iron = Number(ironAmount) || 0;
    const firwood = Number(firwoodAmount) || 0;

    if (silver === 0 && rounds === 0 && iron === 0 && firwood === 0) {
      setError('Please contribute at least one resource');
      return;
    }

    // Check if mercenary has enough resources
    const missing: string[] = [];
    if (silver > activeMercenary.silver) {
      missing.push(`${silver - activeMercenary.silver} silver`);
    }
    if (rounds > activeMercenary.rounds) {
      missing.push(`${rounds - activeMercenary.rounds} rounds`);
    }
    
    const ironItem = activeMercenary.Inventory?.find((inv: any) => inv.Item?.name === 'Iron');
    const firwoodItem = activeMercenary.Inventory?.find((inv: any) => inv.Item?.name === 'Firwood');
    
    if (iron > (ironItem?.quantity || 0)) {
      missing.push(`${iron - (ironItem?.quantity || 0)} Iron`);
    }
    if (firwood > (firwoodItem?.quantity || 0)) {
      missing.push(`${firwood - (firwoodItem?.quantity || 0)} Firwood`);
    }

    if (missing.length > 0) {
      setError(`Missing resources: ${missing.join(', ')}`);
      return;
    }

    setContributing(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post('/api/guild/buildings/contribute', {
        buildingType: getBuildingType(building.name),
        mercenaryId: activeMercenary.id,
        silverAmount: silver,
        roundsAmount: rounds,
        ironAmount: iron,
        firwoodAmount: firwood
      }, { withCredentials: true });

      // Check if nothing was actually deducted (all resources already fully contributed)
      const contributed = response.data.contributed;
      const nothingContributed = contributed &&
        contributed.silver === 0 &&
        contributed.rounds === 0 &&
        Object.values(contributed.materials || {}).every((v: any) => v === 0);

      if (nothingContributed) {
        setSuccess('Nothing needed, this resource is already fully contributed for the next level.');
      } else {
        setSuccess(response.data.message);
      }

      // Reset input amounts
      setSilverAmount('');
      setRoundsAmount('');
      setIronAmount('');
      setFirwoodAmount('');

      if (response.data.upgraded) {
        // Building upgraded, refresh parent and close after a moment
        onUpdate();
        setTimeout(() => onClose(), 1500);
      } else {
        await fetchProgress();
        // Re-fetch mercenary to update inventory (iron/firwood counts)
        await refreshMercenary();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to contribute');
    } finally {
      setContributing(false);
    }
  };

  const handleVisitLibrary = async () => {
    setVisitingLibrary(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(
        '/api/guild/library/visit',
        { mercenaryId: activeMercenary?.id },
        { withCredentials: true }
      );
      setSuccess(response.data.message);
      setTimeout(() => {
        onUpdate();
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to visit library');
    } finally {
      setVisitingLibrary(false);
    }
  };

  const handleNavigateToVault = () => {
    onClose();
    // The vault tab will be activated through the parent component
    const vaultTab = document.querySelector('[data-tab="vault"]') as HTMLElement;
    if (vaultTab) vaultTab.click();
  };

  const getBuildingType = (name: string): string => {
    const mapping: Record<string, string> = {
      'Training Ground': 'trainingGround',
      'Infirmary': 'infirmary',
      'Library': 'library',
      'Warehouse': 'warehouse',
      'Sleeping Hall': 'sleepingHall'
    };
    return mapping[name] || '';
  };

  const getBenefitDetails = (): { current: string; next: string } => {
    const currentLevel = building.level;
    const nextLevel = building.level + 1;
    const buildingType = getBuildingType(building.name);
    const configs: any[] = buildingCosts?.[buildingType] ?? [];

    // Evaluate formula string safely (supports level + basic arithmetic)
    const evalFormula = (formula: string, level: number): number => {
      try {
        return evaluateSafeFormula(formula, level);
      } catch {
        return 0;
      }
    };

    // Fill {value}, {silverValue} (warehouse silver = value * 100), {level} placeholders
    const fillDescription = (description: string, formula: string, level: number): string => {
      const value = evalFormula(formula, level);
      return description
        .replace('{value}', String(value))
        .replace('{silverValue}', (value * 100).toLocaleString('sv-SE'))
        .replace('{level}', String(level));
    };

    const notBuiltLabels: Record<string, string> = {
      trainingGround: 'No bonus (not built)',
      library: 'Cannot visit (not built)',
      infirmary: 'No recovery bonus (not built)',
      warehouse: 'No capacity (not built)',
      sleepingHall: '5 maximum members (base)'
    };

    // Current level benefit
    let current: string;
    if (currentLevel === 0) {
      current = notBuiltLabels[buildingType] ?? building.benefit;
    } else if (configs[currentLevel - 1]) {
      const cfg = configs[currentLevel - 1];
      current = fillDescription(cfg.benefitDescription, cfg.benefitFormula, currentLevel);
    } else {
      current = building.benefit; // fallback while config loads
    }

    // Next level benefit
    let next: string;
    if (configs[nextLevel - 1]) {
      const cfg = configs[nextLevel - 1];
      next = fillDescription(cfg.benefitDescription, cfg.benefitFormula, nextLevel);
    } else {
      next = nextLevel > 10 ? 'Maximum level reached' : 'Loading...';
    }

    return { current, next };
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={onClose}>×</button>
        
        <h2 className={styles.title}>{building.name}</h2>
        <div className={styles.level}>Level {building.level}</div>
        
        <p className={styles.description}>{building.description}</p>
        
        <div className={styles.benefit}>
          <h3>Current Benefit</h3>
          <p>{getBenefitDetails().current}</p>
        </div>
        
        {building.level < guildLevel && (
          <div className={styles.nextBenefit}>
            <h3>Next Level Benefit</h3>
            <p>{getBenefitDetails().next}</p>
          </div>
        )}

        {/* Library Visit Feature */}
        {building.name === 'Library' && building.level > 0 && (
          <div className={styles.librarySection}>
            <button
              className={styles.visitButton}
              onClick={handleVisitLibrary}
              disabled={visitingLibrary}
            >
              {visitingLibrary ? 'Visiting...' : 'Visit Library (Gain XP)'}
            </button>
          </div>
        )}

        {/* Warehouse Navigation */}
        {building.name === 'Warehouse' && building.level > 0 && (
          <div className={styles.warehouseSection}>
            <button
              className={styles.vaultButton}
              onClick={handleNavigateToVault}
            >
              Go to Warehouse
            </button>
          </div>
        )}

        {/* Upgrade Section */}
        {canContribute && (
          <div className={styles.upgradeSection}>
            <h3>Upgrade to Level {building.level + 1}</h3>
            
            {/* Active Mercenary Resources + Contribution Inputs */}
            {mercenary && activeMercenary.guildId && (() => {
              const ironItem = activeMercenary.Inventory?.find((inv: any) => inv.Item?.name === 'Iron');
              const firwoodItem = activeMercenary.Inventory?.find((inv: any) => inv.Item?.name === 'Firwood');
              const resources = [
                { key: 'silver',  label: 'Silver',  avail: activeMercenary.silver,    needed: remaining?.silver,  val: silverAmount,  set: setSilverAmount  },
                { key: 'rounds',  label: 'Rounds',  avail: activeMercenary.rounds,    needed: remaining?.rounds,  val: roundsAmount,  set: setRoundsAmount  },
                { key: 'iron',    label: 'Iron',    avail: ironItem?.quantity || 0,   needed: remaining?.iron,    val: ironAmount,    set: setIronAmount    },
                { key: 'firwood', label: 'Firwood', avail: firwoodItem?.quantity || 0, needed: remaining?.firwood, val: firwoodAmount, set: setFirwoodAmount },
              ];
              return (
                <div className={styles.contributionInputs}>
                  <h4>Contribute ({activeMercenary.name}):</h4>
                  <div className={styles.inputGrid}>
                    {resources.map(({ key, label, avail, needed, val, set }) => {
                      const cap = needed != null && needed > 0 ? Math.min(avail, needed) : avail;
                      const current = Number(val) || 0;
                      const handleChange = (raw: string) => {
                        if (raw === '') { set(''); return; }
                        const n = Math.max(0, Math.min(cap, parseInt(raw, 10) || 0));
                        set(String(n));
                      };
                      return (
                        <div key={key} className={styles.inputCell}>
                          <label className={styles.inputCellLabel}>
                            {label} <span className={styles.inputHint}>{avail} avail{needed != null && needed > 0 ? `, ${needed} needed` : ''}</span>
                          </label>
                          <div className={styles.qtyControl}>
                            <HoldButton
                              className={styles.qtyBtn}
                              onClick={() => set(String(Math.max(0, current - 1)))}
                            >−</HoldButton>
                            <input
                              type="number"
                              min="0"
                              max={cap}
                              placeholder="0"
                              className={styles.qtyInput}
                              value={val}
                              onChange={(e) => handleChange(e.target.value)}
                            />
                            <HoldButton
                              className={styles.qtyBtn}
                              onClick={() => set(String(Math.min(cap, current + 1)))}
                            >+</HoldButton>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {(!mercenary?.guildId || activeMercenary.guildId !== guildId) && (
              <div className={styles.noMercenary}>
                <p>Your active mercenary is not in this guild. Please switch to a guild mercenary to contribute.</p>
              </div>
            )}

            <div className={styles.costs}>
              {(!costPerLevel || progressLoading) ? (
                <p>Loading...</p>
              ) : (
                <>
                  <h4>Progress to Next Level:</h4>
                  {[
                    { label: 'Silver',  contributed: progress.silver,                         total: costPerLevel.silver },
                    { label: 'Rounds',  contributed: progress.rounds,                         total: costPerLevel.rounds },
                    { label: 'Iron',    contributed: progress.materials.Iron || 0,             total: costPerLevel.iron },
                    { label: 'Firwood', contributed: progress.materials.Firwood || 0,         total: costPerLevel.firwood },
                  ].map(({ label, contributed, total }) => {
                    const pct = total > 0 ? Math.min(100, Math.round((contributed / total) * 100)) : 0;
                    return (
                      <div key={label} className={styles.cost}>
                        <div className={styles.costTop}>
                          <span className={styles.costLabel}>{label}:</span>
                          <span className={styles.costValue}>{contributed} <small style={{opacity:0.6}}>/ {total}</small></span>
                        </div>
                        <div className={styles.progressBar}>
                          <div className={styles.progressFill} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            {/* Contribution Inputs handled above in the resources block */}

            <button
              className={styles.contributeButton}
              onClick={handleContribute}
              disabled={contributing || !mercenary?.guildId}
            >
              {contributing ? 'Contributing...' : 'Contribute Resources'}
            </button>
          </div>
        )}

        {building.level >= 10 && (
          <div className={styles.maxLevel}>
            <p>This building is fully built (max level 10)!</p>
          </div>
        )}

        {!canContribute && building.level < 10 && building.level >= guildLevel && (
          <div className={styles.lockedSection}>
            <p className={styles.lockedMessage}>
              Guild must reach level {building.level + 1} before this building can be upgraded further.
            </p>
          </div>
        )}

        {error && <div className={styles.error}>{error}</div>}
        {success && <div className={styles.success}>{success}</div>}
      </div>
    </div>
  );
};

export default BuildingDetailModal;
